/**
 * /sites/:siteId/train — "Teach the judge" (Step 1: capture).
 *
 * Shows ONE comparison at a time: the writer made a few versions of each
 * article, and here you pick the headline / opening line / short answer you
 * like best. Choices are stored to teach the judge your taste later. The
 * live writer and judge are untouched — this only listens.
 *
 * Chunked on purpose: one decision per screen, a small batch per load, and
 * a clear "that's a good chunk" stop so it never feels like homework.
 */

import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { api } from '../lib/api.ts'

interface Option {
  text: string
  variant: number
}
interface Comparison {
  draftId: string
  keyword: string
  elementType: string
  label: string
  options: Option[]
}
interface ScoreItem {
  elementType: string
  label: string
  have: number
  status: 'need_more' | 'scored' | 'inconclusive'
  needMore?: number
  tested?: number
  agree?: number
  agreePct?: number | null
}

export function TrainScreen() {
  const { siteId = 'll' } = useParams()
  const [queue, setQueue] = useState<Comparison[]>([])
  const [remaining, setRemaining] = useState(0)
  const [done, setDone] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [score, setScore] = useState<ScoreItem[] | null>(null)
  const [scoring, setScoring] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const r = await api<{ comparisons: Comparison[]; remaining: number }>(
        `/api/sites/${siteId}/preferences/next?limit=8`,
      )
      setQueue(r.comparisons)
      setRemaining(r.remaining)
    } catch {
      setError('Could not load comparisons. Try again in a moment.')
    } finally {
      setLoading(false)
    }
  }, [siteId])

  useEffect(() => {
    setDone(0)
    load()
  }, [load])

  async function decide(chosenIndex: number | null) {
    const cur = queue[0]
    if (!cur || busy) return
    setBusy(true)
    setError('')
    try {
      await api(`/api/sites/${siteId}/preferences`, {
        method: 'POST',
        body: JSON.stringify({
          draftId: cur.draftId,
          elementType: cur.elementType,
          options: cur.options,
          chosenIndex,
        }),
      })
      setQueue((q) => q.slice(1))
      setRemaining((n) => Math.max(0, n - 1))
      setDone((n) => n + 1)
    } catch {
      setError('Could not save your pick — try again.')
    } finally {
      setBusy(false)
    }
  }

  async function checkScore() {
    setScoring(true)
    setError('')
    try {
      const r = await api<{ results: ScoreItem[] }>(`/api/sites/${siteId}/preferences/score`)
      setScore(r.results)
    } catch {
      setError('Could not check the score — try again in a moment.')
    } finally {
      setScoring(false)
    }
  }

  const current = queue[0]

  return (
    <div>
      <div className="page-header">
        <h1>Teach the judge</h1>
        <span className="subtitle">
          {done > 0 ? `${done} saved this session · ` : ''}
          {remaining} to review
        </span>
      </div>

      <p style={{ color: 'var(--text-muted)', maxWidth: 680, marginTop: '-0.5rem' }}>
        The writer makes a few versions of each piece. Pick the one you like best — your
        choices teach the judge to choose the way you would. A handful is plenty; you can
        stop anytime.
      </p>

      <div className="card" style={{ maxWidth: 720, margin: '1rem 0', gap: '0.6rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: '0.9rem' }}>How well it's learning your taste</strong>
          <button type="button" className="btn" disabled={scoring} onClick={checkScore}>
            {scoring ? 'Checking…' : score ? 'Re-check' : 'Check now'}
          </button>
        </div>
        {score ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.85rem' }}>
            {score.map((s) => (
              <div key={s.elementType} style={{ color: 'var(--text-muted)' }}>
                <strong style={{ color: 'var(--text)' }}>{s.label}:</strong>{' '}
                {s.status === 'scored' && s.agreePct != null ? (
                  <span>
                    agrees with you{' '}
                    <strong style={{ color: s.agreePct >= 80 ? 'var(--ok)' : 'var(--text)' }}>{s.agreePct}%</strong>{' '}
                    ({s.agree}/{s.tested} checked) · {s.have} picks
                  </span>
                ) : s.status === 'need_more' ? (
                  <span>
                    {s.have} picks — about {s.needMore} more to measure
                  </span>
                ) : (
                  <span>{s.have} picks — couldn't measure yet</span>
                )}
              </div>
            ))}
            <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: '0.25rem' }}>
              80%+ on something means the judge is matching you there. Read-only check — the live judge stays untouched.
            </div>
          </div>
        ) : (
          !scoring && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-faint)' }}>
              See how often the judge would pick what you picked. Costs a few cents to run.
            </div>
          )
        )}
      </div>

      {error && (
        <div className="error-state" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading…</div>
      ) : current ? (
        <div className="card" style={{ maxWidth: 720, gap: '1rem' }}>
          <div className="card-meta">
            <span>
              <strong>{current.label}</strong>
            </span>
            <span>for: {current.keyword}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {current.options.map((opt, i) => (
              <button
                key={i}
                type="button"
                disabled={busy}
                onClick={() => decide(i)}
                style={{
                  textAlign: 'left',
                  padding: '0.85rem 1rem',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-strong)',
                  background: 'var(--bg-muted)',
                  color: 'var(--text)',
                  fontSize: '0.95rem',
                  lineHeight: 1.45,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--accent)'
                  e.currentTarget.style.background = 'var(--accent-soft)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-strong)'
                  e.currentTarget.style.background = 'var(--bg-muted)'
                }}
              >
                {opt.text}
              </button>
            ))}
          </div>
          <div className="editor-actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>
              Click the one you prefer
            </span>
            <button type="button" className="btn" disabled={busy} onClick={() => decide(null)}>
              Skip — they're about the same
            </button>
          </div>
        </div>
      ) : remaining > 0 ? (
        <div className="empty" style={{ maxWidth: 720 }}>
          <p style={{ fontSize: '1rem', color: 'var(--text)' }}>
            {done > 0 ? `Nice — ${done} picks saved. That's a good chunk.` : 'Ready when you are.'}
          </p>
          <p>{remaining} more whenever you feel like it. No rush.</p>
          <button type="button" className="btn btn-primary" style={{ marginTop: '0.75rem' }} onClick={load}>
            Keep going
          </button>
        </div>
      ) : (
        <div className="empty" style={{ maxWidth: 720 }}>
          {done > 0 ? (
            <>
              <p style={{ fontSize: '1rem', color: 'var(--text)' }}>All caught up — {done} picks saved. 🎉</p>
              <p>The judge has more to learn from. Come back after the next batch of articles is written.</p>
            </>
          ) : (
            <>
              <p style={{ fontSize: '1rem', color: 'var(--text)' }}>Nothing to compare yet.</p>
              <p>
                Each article the writer finishes comes in a few versions. Once a couple are
                ready to review, their headlines and openings show up here to choose between.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
